import { gameLogger } from '../../utils/GameLogger';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { DebugFCT } from '../debug/DebugFCT';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { EnhancedParticleManager } from './EnhancedParticleManager';
import { FloatingTextConfig } from './types/EffectTypes';
import type { AnimatedTextOptions } from './text/types';
import { createGraphics } from '../rendering/utils/Graphics';

// Sub-Managers
import { VisualLifecycleStore } from '../diagnostics/VisualLifecycleStore';
import { BeamLinkStore } from '../diagnostics/BeamLinkStore';

// Data-Driven Effects
import { VFXEffectPlayer } from './VFXEffectPlayer';
import { SummonVFXHandler } from './handlers/SummonVFXHandler';
import { SwingBeamVFXHandler } from './handlers/SwingBeamVFXHandler';
import { StatusVFXHandler } from './handlers/StatusVFXHandler';
import { RemoteCastVFXHandler } from './handlers/RemoteCastVFXHandler';
import { LevelUpVFXHandler } from './handlers/LevelUpVFXHandler';
import { CombatEventVFXHandler } from './handlers/CombatEventVFXHandler';
import { VFXEffectRegistry } from './VFXEffectRegistry';
import { DEFAULT_VFX_EFFECTS } from './config/VFXEffectDefs';
import { DecalLayer } from './decals/DecalLayer';
import { ABILITY_VFX_EFFECTS } from './config/VFXAbilityDefs';
import { registerNamedAbilityEffects } from './config/namedEffectGenerator';
import { getAbilityVFX } from './config/AbilityVFXMap';
import { SCREEN_EFFECTS_CONFIG } from '../../config/effects/screenEffects.config';
import { TrailSystem } from './players/TrailSystem';
import { TRAIL_FX_CONFIG } from '../../config/abilities/trailFx.config';
import { PactDoorVFXHandler } from './handlers/PactDoorVFXHandler';

/** Persistent-tracker key for a cast-lifecycle spawn (channel visuals) —
 *  shared between the spawn route and the cancel teardown (beam spec §6). */
function castInstanceId(casterId: string, abilityId: string): string {
  return `cast:${casterId}:${abilityId}`;
}

/**
 * `summon_<abilityId>_<ts>` → `<abilityId>` (SummonBehavior mint format,
 * SummonBehavior.ts:53). Ability ids can themselves contain `_`, so strip the
 * fixed `summon_` prefix and the trailing `_<digits>` timestamp; the remainder
 * is the abilityId. Returns null for any id that isn't a summon-zone instance.
 * Used to key the `zone_expired` lifecycle row to its ability (A7-ZONES).
 */
export function deriveAbilityIdFromInstanceId(instanceId: string): string | null {
  if (!instanceId.startsWith('summon_')) return null;
  const rest = instanceId.slice('summon_'.length);
  const m = rest.match(/^(.+)_\d+$/);
  return m ? m[1] : null;
}

/**
 * VFXSystem - High-level orchestrator for visual effects
 * Routes game events to data-driven visual effects via VFXEffectPlayer
 */
export class VFXSystem {
  private static instance: VFXSystem;
  private scene: unknown;
  private particleManager: EnhancedParticleManager;
  private effectPlayer: VFXEffectPlayer;
  private initialized = false;
  private eventUnsubscribers: Array<() => void> = [];
  // Decomposed event-domain handlers (one module per VFX domain).
  private summonHandler = new SummonVFXHandler(() => this.scene);
  private swingBeamHandler = new SwingBeamVFXHandler(() => this.scene);
  private pactDoorHandler = new PactDoorVFXHandler();
  private statusHandler: StatusVFXHandler;
  private remoteHandler: RemoteCastVFXHandler;
  private levelUpHandler: LevelUpVFXHandler;
  private combatHandler: CombatEventVFXHandler;

  private constructor(scene: unknown) {
    this.scene = scene;
    this.particleManager = EnhancedParticleManager.getInstance(scene);
    this.effectPlayer = new VFXEffectPlayer(scene);
    this.statusHandler = new StatusVFXHandler(this.effectPlayer);
    this.combatHandler = new CombatEventVFXHandler(this.effectPlayer);
    this.remoteHandler = new RemoteCastVFXHandler({
      effectPlayer: this.effectPlayer,
      spawnEffectFromData: (data) => this.spawnEffectFromData(data),
      spawnEffect: (effectId, x, y) => this.spawnEffect(effectId, x, y),
    });
    this.levelUpHandler = new LevelUpVFXHandler({
      playById: (effectId, x, y, overrides) => this.effectPlayer.playById(effectId, x, y, overrides),
    });

    // Register all data-driven effect definitions
    VFXEffectRegistry.registerAll(DEFAULT_VFX_EFFECTS);
    VFXEffectRegistry.registerAll(ABILITY_VFX_EFFECTS);
    // Realize every TOML cast_effect/impact_effect name as corresponding data
    // (alias to the curated map effect, else school-derived) — T3.1.
    registerNamedAbilityEffects();
  }

  public static getInstance(scene?: unknown): VFXSystem {
    if (!VFXSystem.instance && scene) {
      VFXSystem.instance = new VFXSystem(scene);
    } else if (VFXSystem.instance && scene) {
      VFXSystem.instance.setScene(scene);
    }
    return VFXSystem.instance;
  }

  public setScene(scene: unknown): void {
    if (this.scene !== scene) {
      gameLogger.debug('vfx', '🎨 [VFXSystem] Scene changed, refreshing references...');
      const wasInitialized = this.initialized;
      this.cleanup();
      this.scene = scene;
      this.particleManager = EnhancedParticleManager.getInstance(scene);
      this.effectPlayer.setScene(scene);
      // If listeners were already registered, re-register them on the new
      // scene so remote-cast subscriptions are not silently dropped when the
      // GameScene object changes (e.g. multiple Phaser scene starts during boot).
      if (wasInitialized) {
        this.initialize();
      }
    }
  }

  public initialize(): void {
    if (this.initialized) return;
    gameLogger.info('vfx', '🎨 VFXSystem initialized');
    this.setupEventListeners();
    this.initialized = true;
  }

  private setupEventListeners(): void {
    // Unsubscribe any previous listeners (e.g. after scene change)
    this.teardownEventListeners();

    const sub = (event: string, handler: (data: any) => void) => {
      this.eventUnsubscribers.push(EventBus.getInstance().onDynamic(event, handler));
    };

    sub('vfx:spawn', (data) => this.spawnEffectFromData(data));
    sub('vfx:blink', (data) => this.playBlinkEffect(data));

    // Cast/channel teardown (beam-builders spec §6, integration F):
    // CastTimelineSystem announces early cast death (interrupt / movement /
    // overwrite); we destroy the cast's tracked channel visuals immediately.
    // Before this, an interrupted channel burned its beam full-duration.
    sub('combat:cast_cancelled', (data) => this.onCastCancelled(data));

    // Dash afterimages (trails spec §2.2) — sprite-snapshot ghosts via the
    // pooled TrailSystem; falls back to the legacy trail one-shot def when
    // no texture key resolves (degrade, never a dead dash).
    sub('vfx:dash_ghost', (data) => this.onDashGhost(data));
    sub('vfx:dash_ghost_line', (data) => this.onDashGhostLine(data));

    // Persistent status/orbit events
    sub('vfx:lucky_seven_orbit', (data) => this.effectPlayer.playPersistent(`lucky_seven_${data.casterId}`, 'lucky_seven_orbit', data.x, data.y, data));
    sub('vfx:lucky_seven_fire', (data) => this.effectPlayer.destroyPersistentPrimitive(`lucky_seven_${data.casterId}`, data.index));
    sub('vfx:arcane_barrage_orbit', (data) => this.effectPlayer.playPersistent(`arcane_barrage_${data.casterId}`, 'arcane_barrage_orbit', data.x, data.y, data));
    sub('vfx:arcane_barrage_fire', (data) => this.effectPlayer.destroyPersistentPrimitive(`arcane_barrage_${data.casterId}`, data.index));
    sub('vfx:reload_stars', (data) => this.effectPlayer.playById('reload_stars', data.x, data.y, data));

    // Zone lifetime ownership (zone-builders spec §4, contract rule 1): the
    // placing behavior owns its zone via instanceId. SummonBehavior emits
    // `vfx:summon_expired` at duration end; that must stop the TRACKED
    // persistent zone VFX (routed via spawnEffectFromData → playPersistent),
    // not just fade the placement ring (SummonVFXHandler handles the ring
    // separately below). Before this, a registered looping summon def had NO
    // stop path — ice_wall (repeat:-1, no duration) leaked until scene swap.
    sub('vfx:summon_expired', (data) => {
      const instanceId = data?.instanceId ? String(data.instanceId) : null;
      if (!instanceId) return;
      // A7-ZONES: record a render-truth `zone_expired` witness BEFORE stopping.
      // The scheduled stop firing at duration is positive evidence a leaking
      // zone cannot fake — the capture window can't watch a zone die by row
      // absence (long zones outlive the burst), so we emit the event instead.
      // Anchor comes from the still-live persistent shape (effectId === the
      // instanceId in the effect summary), read before teardown clears it.
      const anchor = this.effectPlayer.getActiveEffectSummary().find((r) => r.effectId === instanceId);
      VisualLifecycleStore.record({
        type: 'zone_expired',
        label: `${instanceId} expired`,
        x: anchor?.x ?? 0,
        y: anchor?.y ?? 0,
        abilityId: deriveAbilityIdFromInstanceId(instanceId),
        meta: { instanceId },
      });
      this.effectPlayer.stopPersistentEffect(instanceId);
    });

    // Decomposed behavior-VFX domains
    this.swingBeamHandler.register(sub);
    this.pactDoorHandler.register(sub);
    this.summonHandler.register(sub);
    this.statusHandler.register(sub);
    this.remoteHandler.register(sub);
    this.combatHandler.register(sub);

    // Standalone screen effect events
    sub('vfx:screen_shake', (data) => this.screenShake(data.intensity, data.duration));
    sub('vfx:screen_flash', (data) => this.screenFlash(data.color, data.duration));

    // K24 (2026-07-17): in-place server map switches (GM-event warp) swap the
    // level on the SAME scene — no navigation, no setScene/cleanup — so old-map
    // VFX (in-flight bursts, repeat:-1 leaks, placed summon zones, a running
    // shake) survived into the new map. Sweep transients the moment the switch
    // starts. The navigate path needs nothing here: it's a full page load.
    sub(EVENTS.MAP_SERVER_SWITCH_START, (data) => this.sweepTransient(`server_map_switch:${data?.mapId ?? '?'}`));

    // Level-up: on the leveling character, local or remote — no screen effect.
    this.levelUpHandler.register(sub);

    // Combat feedback: low HP vignette warning
    sub('net:periodicSync', (data) => {
      if (data.health && data.maxHealth) {
        const warn = SCREEN_EFFECTS_CONFIG.lowHpWarning;
        const hpPercent = data.health / data.maxHealth;
        if (hpPercent < warn.hpFraction && hpPercent > 0) {
          this.screenFlash(warn.color, warn.durationMs);
        }
      }
    });
  }

  private teardownEventListeners(): void {
    this.eventUnsubscribers.forEach(unsub => unsub());
    this.eventUnsubscribers = [];
    this.remoteHandler.teardown();
    this.statusHandler.teardown();
    // A subsequent scene initialize must re-register listeners.
    this.initialized = false;
  }

  private spawnEffectFromData(data: { effectId: string, x: number, y: number, targetId?: string, abilityId?: string, [key: string]: any }): void {
    DebugFCT.info('vfx', data.effectId, data.x, data.y);
    // This funnel is where data-driven cast VFX actually flow (behaviors emit
    // 'vfx:spawn' with the TOML cast_effect at execute time) — onAbilityCast's
    // 'ability:cast' event has no emitters, so recording only there left the
    // Visual Lifecycle monitor blind to every successful cast (DJ 2026-06-12).
    VisualLifecycleStore.record({
      type: 'ability_cast_vfx',
      label: `${data.abilityId ?? '?'} → ${data.effectId}`,
      x: data.x, y: data.y,
      abilityId: data.abilityId ?? null,
      targetId: data.targetId ?? null,
      meta: { effectId: data.effectId },
    });
    let resolvedId = data.effectId;

    if (!VFXEffectRegistry.has(resolvedId) && data.abilityId) {
      const mapping = getAbilityVFX(data.abilityId);
      if (mapping?.castVfxId && VFXEffectRegistry.has(mapping.castVfxId)) {
        resolvedId = mapping.castVfxId;
      } else if (VFXEffectRegistry.has(`${data.abilityId}_aura`)) {
        // TOML `aura_color` registers `${abilityId}_aura` at ability build
        // time — lets remote aura plays (resolveRemoteAbilityVfx feeds an
        // unregistered raw `cast_effect` like "fire_aura" here) land on the
        // same registered aura the local status path renders.
        resolvedId = `${data.abilityId}_aura`;
      }
    }

    if (VFXEffectRegistry.has(resolvedId)) {
      // Pass targetId through to enable FollowerSystem tracking. A payload
      // with casterId and no targetId rides the caster (wind-up glows depend
      // on this) — EXCEPT when the emitter says `follow: false`: positional
      // one-shots (dash origin trails, teleport departure puffs) must stay
      // where they spawned, not chase the dasher to the destination
      // (U-recent-delta-2: blade_rush trail rendered on the wrong side).
      const playOverrides = {
        ...data,
        effectId: resolvedId,
        targetId: data.targetId || (data.follow === false ? undefined : data.casterId),
      };
      // Cast-lifecycle spawns (channels) are tracked per (caster, ability) so
      // combat:cast_cancelled can destroy them mid-channel (beam spec §6).
      // The duration kill still owns the natural-completion path.
      const persistId = (typeof data.instanceId === 'string' && data.instanceId)
        ? data.instanceId
        : (data.castLifecycle === true && data.casterId && data.abilityId
          ? castInstanceId(data.casterId, data.abilityId)
          : null);
      if (persistId) {
        // Zone lifetime contract (zone-builders spec §4, contract rule 1): a
        // spawn carrying an instanceId is a world-anchored persistent
        // (SummonBehavior zones/traps/totems). Route through playPersistent
        // so the paired `vfx:summon_expired` (and early cancels / map-switch
        // sweeps) can stop it. The previous plain playById IGNORED
        // instanceId — which is how a registered repeat:-1 def (ice_wall)
        // had no stop path and leaked until scene swap.
        this.effectPlayer.playPersistent(persistId, resolvedId, data.x, data.y, playOverrides);
      } else {
        this.effectPlayer.playById(resolvedId, data.x, data.y, playOverrides);
      }
    } else {
      // Fallback to legacy particles
      this.spawnEffect(data.effectId, data.x, data.y);
    }
  }

  /**
   * A cast died early (interrupt / movement-cancel / overwrite) — destroy its
   * tracked channel visuals NOW instead of letting them burn their authored
   * duration (beam-builders spec §6, integration F). No-op for casts with no
   * tracked visuals (stopPersistentEffect on a missing key does nothing).
   */
  private onCastCancelled(data: { casterId?: string; abilityId?: string; reason?: string }): void {
    if (!data?.casterId || !data?.abilityId) return;
    this.effectPlayer.stopPersistentEffect(castInstanceId(data.casterId, data.abilityId));
  }

  // ── Dash afterimage ghosts (trails-and-afterimages spec §2.2) ─────

  /** Local player's sprite texture key — a texture-key clone IS a faithful
   *  snapshot (static textures, no frame animation — spec §1.6). */
  private localGhostContext(): { textureKey: string | null; flipX: boolean } {
    try {
      const pm = ServiceRegistry.getInstance().get('EntitySystem')?.getPlayerManager?.();
      return {
        textureKey: pm?.getPlayerTextureKey?.() ?? null,
        flipX: pm?.getPlayer?.()?.facing === 'left',
      };
    } catch {
      return { textureKey: null, flipX: false };
    }
  }

  /** One travel-dash afterimage tick. Position is physics top-left → +20
   *  sprite-center offset (the PlayerRenderer convention). */
  private onDashGhost(data: {
    x: number; y: number; abilityId?: string; fallbackEffectId?: string;
    directive?: { tint?: number; lifeMs?: number; startAlpha?: number; blend?: 'add' | 'normal' };
  }): void {
    const { textureKey, flipX } = this.localGhostContext();
    const d = data.directive;
    if (textureKey && TrailSystem.spawnGhost({
      x: data.x + 20, y: data.y + 20,
      textureKey,
      tint: d?.tint, lifeMs: d?.lifeMs, startAlpha: d?.startAlpha, blend: d?.blend,
      flipX,
      abilityId: data.abilityId,
    })) {
      return;
    }
    // Degrade: the pre-ghost interval one-shot on the body centre (plan 29 F7).
    if (data.fallbackEffectId) {
      this.spawnEffectFromData({
        effectId: data.fallbackEffectId,
        x: data.x + 20, y: data.y + 20,
        abilityId: data.abilityId,
        duration: TRAIL_FX_CONFIG.ghost.lifeMs,
        follow: false,
      });
    }
  }

  /** Instant blinks (travelMs 0 — Blink/Shimmer/Disengage): ghosts
   *  interpolated along the blink line give teleports their first motion
   *  read (they had NO trail before, spec §1.3). */
  private onDashGhostLine(data: {
    playerId: string; fromX: number; fromY: number; toX: number; toY: number;
    abilityId?: string; flipX?: boolean;
    directive?: { tint?: number; count?: number; lifeMs?: number; startAlpha?: number; blend?: 'add' | 'normal' };
  }): void {
    const { textureKey, flipX } = this.localGhostContext();
    if (!textureKey) return;
    const d = data.directive;
    const count = Math.max(1, d?.count ?? TRAIL_FX_CONFIG.ghost.count);
    const baseAlpha = d?.startAlpha ?? TRAIL_FX_CONFIG.ghost.startAlpha;
    for (let i = 1; i <= count; i++) {
      const t = i / (count + 1);
      TrailSystem.spawnGhost({
        x: data.fromX + (data.toX - data.fromX) * t + 20,
        y: data.fromY + (data.toY - data.fromY) * t + 20,
        textureKey,
        tint: d?.tint, lifeMs: d?.lifeMs, blend: d?.blend,
        // Ramp toward the destination — the freshest ghost reads hottest.
        startAlpha: baseAlpha * (0.5 + 0.5 * t),
        flipX: data.flipX ?? flipX,
        abilityId: data.abilityId,
      });
    }
  }

  private playBlinkEffect(data: { x: number, y: number, targetX: number, targetY: number, color?: number }): void {
    DebugFCT.info('vfx', 'Blink', data.x, data.y);
    this.effectPlayer.playById('blink_out', data.x, data.y, { color: data.color });
    this.effectPlayer.playById('blink_in', data.targetX, data.targetY, { color: data.color });
  }

  // ===== PUBLIC API (PROXIED TO PLAYER) =====

  /**
   * K24 (2026-07-17): transient-VFX sweep for in-place server map switches
   * (GM-event warp). The navigate path gets teardown for free (full page
   * load); the in-place path (`net:message:mapChanged` →
   * SceneEventManager.handleServerMapChanged) swaps the level on the same
   * scene, so anything the old map spawned — in-flight bursts, repeat:-1
   * hand-rolls (the ice_wall class), placed summon zones, a running screen
   * shake — would haunt the new map. Reuses the existing per-manager clears.
   *
   * Deliberately NOT swept:
   *  - Entity-attached PersistentEffectTracker entries (status auras, mana
   *    shield, orbit charge stacks) — entities SURVIVE an in-place switch
   *    and aura visuals are event-driven with no re-derive path. sweepStale's
   *    60s ceiling backstops genuine tracker leaks. WORLD-anchored tracker
   *    entries (`summon_*` zone instances, zone-builders spec §4) ARE swept —
   *    they belong to the old map's ground (see sweepTransientShapes).
   *  - Particles — bursts self-expire; named emitters are shared registrations
   *    that must stay usable after the switch.
   *  - Atmosphere layers — re-mount themselves via LEVEL_LOADED, which
   *    MapSystem.loadLevel emits on this very path.
   */
  public sweepTransient(reason: string): void {
    const released = this.effectPlayer.sweepTransientShapes();
    this.summonHandler.clearAll();
    const cameraSystem = ServiceRegistry.getInstance().get('CameraSystem', { optional: true });
    cameraSystem?.getManager()?.stopShake();
    gameLogger.info('vfx', `🧹 [VFXSystem] Transient sweep (${reason}): ${released} shapes released, summon rings cleared, shake reset`);
  }

  public screenShake(intensity: number, duration: number): boolean {
    // Phase H9 Batch 2 (2026-05-08): route through CameraSystem so the
    // shake is actually ticked each frame. The previous ScreenEffectManager
    // path mutated camera scroll directly via an update() that nothing
    // ever called — 16+ ability files emitted into a dead path. CameraManager
    // delegates to CameraEffects which IS pumped by CameraSystem.update.
    // CameraManager records lifecycle truth only after CameraEffects accepts
    // the request; this request layer must never claim rendering first.
    const cameraSystem = ServiceRegistry.getInstance().get('CameraSystem', { optional: true });
    return cameraSystem?.getManager()?.shake(intensity, duration) ?? false;
  }

  public screenFlash(
    color: number = SCREEN_EFFECTS_CONFIG.flash.color,
    duration: number = SCREEN_EFFECTS_CONFIG.flash.durationMs,
  ): void {
    this.effectPlayer.screenEffects.screenFlash(color, duration);
  }

  public screenTint(color: number, duration: number): void {
    this.effectPlayer.screenEffects.screenTint(color, duration);
  }

  public showFloatingText(text: string, x: number, y: number, config: FloatingTextConfig): void {
    this.effectPlayer.floatingText.showFloatingText(text, x, y, config);
  }

  public showDamageNumber(amount: number, x: number, y: number, isCritical: boolean = false): void {
    this.combatHandler.showDamageNumber(amount, x, y, isCritical);
  }

  public showHealingNumber(amount: number, x: number, y: number): void {
    this.combatHandler.showHealingNumber(amount, x, y);
  }

  public showAbilityName(abilityName: string, x: number, y: number): void {
    this.combatHandler.showAbilityName(abilityName, x, y);
  }

  public showXPGain(amount: number, x: number, y: number): void {
    this.effectPlayer.floatingText.showXPGain(amount, x, y);
  }

  public showAnimatedText(text: string, x: number, y: number, options: Omit<AnimatedTextOptions, 'text' | 'x' | 'y'>): void {
    this.effectPlayer.animatedText.show({ ...options, text, x, y });
  }

  // ===== COMBAT HANDLERS =====

  /**
   * Predicted-damage hook: fires off the optimistic local
   * `combat:damage` event from DamageBridge. Plays the visual hit VFX
   * (splash / particles / hit_physical) for the "feels like I hit
   * something" feedback, but does NOT spawn a floating damage number
   * — that's owned exclusively by `DamagePresentationService` which
   * subscribes to the server-confirmed events. Without this split, every
   * hit produced two FCTs (one predicted from here, one confirmed from
   * CombatManager). See DamagePresentationService.ts header for the
   * full rationale (2026-04-30 refactor).
   */

  /**
   * Find a mob/NPC's current position by ID.
   * Checks MobManager first (server-synced mobs), then NPCManager sprites as fallback.
   */
  public spawnEffect(effectId: string, x: number, y: number): void {
    if (effectId === 'smoke') {
      this.particleManager.spawnSmoke(x, y);
    } else {
      this.particleManager.spawnBurst(effectId, x, y, 5);
    }
  }

  public update(time: number, delta: number): void {
    this.statusHandler.update();
    this.effectPlayer.update(time, delta);

    // Armed-summon proximity triggers live in the summon handler.
    this.summonHandler.update();
  }

  public cleanup(): void {
    this.teardownEventListeners();
    this.effectPlayer.setScene(this.scene); // Triggers internal manager cleanup
    // Decals live on their own below-entity layer outside the effect-player
    // managers — full scene teardown must drop them (in-place map switches
    // are handled separately by the layer's own MAP_SERVER_SWITCH_START sub).
    DecalLayer.disposeIfLive();
  }

  // ── Screen effects ──

  public playById(id: string, x?: number, y?: number, overrides?: Record<string, any>): void {
    const config = VFXEffectRegistry.get(id);
    if (config) {
      this.effectPlayer.play(config, x ?? 0, y ?? 0, overrides);
    }
  }

  /**
   * Hitstop (impact freeze-frame) — currently an HONEST NO-OP (K22).
   *
   * The Phaser-era implementation set `scene.time.timeScale = 0`, but the
   * Pixi substrate's Scene has no `.time` (src/game/host/Scene.ts), so the
   * write never froze anything — yet a 'hitstop' lifecycle event was still
   * recorded, so QA sidecars reported freezes that never rendered
   * (docs/plans/fx-grammar-specs/hit-feel-kit.md §1.3).
   *
   * Ruling (spec §2.3, sequenced at §7 step 5): the replacement is a
   * per-sprite micro-freeze (HitFeelFreezeStore consulted by MobRenderer /
   * PlayerRenderer; local player never frozen) — NOT a global time freeze.
   * Until that store exists, no freeze can apply, so NO 'hitstop' lifecycle
   * event may be recorded here: sidecars record only what actually
   * rendered. Callers (triggerImpact / triggerFinisher)
   * stay wired — this method is the seam where the freeze store lands.
   */
  public hitstop(_durationMs: number): void {
    // HONEST_HITSTOP_NOOP (K22): no freeze applies on the Pixi substrate;
    // recording a 'hitstop' event here would be fiction. Re-add recording
    // only when an actual freeze is applied (spec §7 step 5).
  }

  /**
   * Slow time globally for `durationMs`, then restore. Scale > 0 keeps
   * scene.time progressing, but using setTimeout for restore is still
   * correct (predictable real-world duration regardless of scale).
   */
  public slowMo(scale: number, durationMs: number): void {
    // Phase H10 Arc λ.11 guard: pure IScene has no Phaser time manager.
    const sceneTime = (this.scene as unknown as { time?: { timeScale?: number } })?.time;
    const prevScale = sceneTime?.timeScale ?? 1;
    if (sceneTime) {
      try { sceneTime.timeScale = scale; } catch { /* no-op */ }
    }
    setTimeout(() => {
      if (this.scene && sceneTime) {
        try { sceneTime.timeScale = prevScale; } catch { /* no-op */ }
      }
    }, durationMs);
  }

  /**
   * Chromatic aberration overlay — intentional no-op.
   *
   * Proper implementation requires a Phaser post-processing shader pipeline
   * that doesn't exist in this codebase yet. CombatFeedbackManager calls
   * this from triggerImpact (heavy intensity) and triggerFinisher, but the
   * effect is optional — the existing vignette + hitstop + screenShake
   * combo carries the "hit feels good" weight without it.
   *
   * If/when a shader pipeline ships, replace this with the real effect.
   * Callers stay unchanged.
   */
  public chromaticAberration(_intensity: number, _durationMs: number): void {
    // Intentional no-op. See JSDoc above.
  }

  public vignette(intensity: number, durationMs: number): void {
    // Dark border overlay that fades in + back out — simulates vignette
    // without shaders. Screen-space (camera-fixed) via screenSpace flag.
    // Phase H Stage 5s: substrate-aware via createGraphics on the UI
    // layer + rAF-driven fade animation. Phase H10 Arc γ: viewport reads
    // through ICamera via ServiceRegistry.
    const cfg = SCREEN_EFFECTS_CONFIG.vignette;
    const cam = ServiceRegistry.getInstance()
      .get('CameraSystem', { optional: true })
      ?.getManager()
      ?.getICamera();
    const w = cam?.width ?? cfg.fallbackWidth;
    const h = cam?.height ?? cfg.fallbackHeight;
    const border = Math.min(w, h) * cfg.borderFraction;
    const fillAlpha = intensity * cfg.fillAlphaMultiplier;

    const overlay = createGraphics(this.scene, { depth: cfg.depth, screenSpace: true });
    // Four border rects (top, bottom, left, right). Drawn at full
    // intensity; the outer setAlpha drives the visible fade-in/out.
    overlay.fillRect(0, 0, w, border, 0x000000, fillAlpha);
    overlay.fillRect(0, h - border, w, border, 0x000000, fillAlpha);
    overlay.fillRect(0, 0, border, h, 0x000000, fillAlpha);
    overlay.fillRect(w - border, 0, border, h, 0x000000, fillAlpha);
    overlay.setAlpha(0);

    // Phases: rise(0..risePhase), hold(..+holdPhase), fall(remainder) —
    // rise + hold + fall = 1.0 × durationMs (fall mirrors rise).
    const riseMs = durationMs * cfg.risePhase;
    const holdMs = durationMs * cfg.holdPhase;
    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      let alpha: number;
      if (elapsed < riseMs) {
        alpha = elapsed / riseMs;
      } else if (elapsed < riseMs + holdMs) {
        alpha = 1;
      } else if (elapsed < durationMs) {
        alpha = 1 - (elapsed - riseMs - holdMs) / riseMs;
      } else {
        overlay.destroy();
        return;
      }
      overlay.setAlpha(alpha);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  public getEffectPlayer(): VFXEffectPlayer {
    return this.effectPlayer;
  }

  /** Pull-only beam truth for the ?testhooks=1 live-probe harness. */
  public getActiveBeamCount(): number {
    return this.effectPlayer.getActiveBeamCount();
  }

  /** Number of retained endpoint samples (newest-last ring buffer). */
  public getBeamSampleCount(): number {
    return BeamLinkStore.all().length;
  }

  /**
   * Render-truth snapshot of VFX currently on screen — consumed by the
   * test-only `__h2kRenderState()` hook (Lane 5 remote-AoE-VFX parity). Returns
   * one coarse row per live effect (kind/instanceId/world-x/world-y plus the
   * owning effect/ability id when known). Read-only; computed on demand, no
   * per-frame cost. Two sources are flattened:
   *   - the SummonVFXHandler's ground rings (trap/totem/aura), and
   *   - everything the VFXEffectPlayer's subsystems are tracking
   *     (persistent/orbiter/follower shapes) via getActiveEffectSummary().
   * This is what lets observer B assert that A's remote AoE VFX rendered near
   * A's position (VFXSystem.onRemoteAbilityCasted → resolveRemoteAbilityVfx →
   * spawnEffectFromData → the player's subsystems).
   */
  public getRenderedEffects(): Array<{
    kind: string;
    instanceId: string;
    x: number;
    y: number;
    ownerId?: string | null;
    abilityId?: string | null;
    effectId?: string | null;
  }> {
    const out: Array<{
      kind: string;
      instanceId: string;
      x: number;
      y: number;
      ownerId?: string | null;
      abilityId?: string | null;
      effectId?: string | null;
    }> = [];

    // Summon ground rings (trap/totem/aura) — owned by the summon handler.
    for (const row of this.summonHandler.getRenderedRings()) {
      out.push(row);
    }

    // Player-tracked shapes (persistent / orbiter / follower / shape-budget).
    for (const row of this.effectPlayer.getActiveEffectSummary()) {
      out.push(row);
    }

    return out;
  }

}
